/**
 * Phase 5: the review queue, disputes, remote approvals, brokers and commission, customers.
 */

import { api } from '../api.js';
import {
  approvalRequestListTag,
  approvalRequestTag,
  brokerListTag,
  commissionBalanceTag,
  commissionStatementTag,
  customerListTag,
  disputeListTag,
  disputeNoteListTag,
  disputeTag,
  reviewItemListTag,
  reviewQueueSummaryTag,
  brokerCommissionTag,
} from '../tags.js';
import type { Paged, ShopId } from '../../types/api.js';
import type {
  ApprovalRequest,
  ApprovalRequestId,
  Broker,
  BrokerId,
  CommissionBalance,
  CommissionBasis,
  CommissionPayoutMethod,
  CommissionScope,
  CommissionStatementLine,
  Customer,
  CustomerId,
  Dispute,
  DisputeId,
  DisputeNote,
  DisputeType,
  PaymentDisputeCandidate,
  ReviewItem,
  ReviewItemId,
  ReviewQueueSummary,
  ReviewSeverity,
  ReviewType,
  UnitHistory,
  BrokerCommissionRule,
  BrokerPayout,
} from '../../types/oversight.js';

// ── Part A: review queue ─────────────────────────────────────────────────────

export type ListReviewItemsParams = {
  readonly status?: 'open' | 'resolved' | 'dismissed' | 'all' | undefined;
  readonly reviewType?: ReviewType | undefined;
  readonly shopId?: ShopId | undefined;
  readonly severity?: ReviewSeverity | undefined;
  readonly minAgeDays?: number | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export const reviewApi = api.injectEndpoints({
  endpoints: (build) => ({
    listReviewItems: build.query<Paged<ReviewItem>, ListReviewItemsParams>({
      query: (params) => ({ url: '/review-items', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [reviewItemListTag()],
    }),

    getReviewQueueSummary: build.query<ReviewQueueSummary, void>({
      query: () => '/review-items/summary',
      providesTags: [reviewQueueSummaryTag()],
    }),

    resolveReviewItem: build.mutation<void, { id: ReviewItemId; resolutionNote: string }>({
      query: ({ id, resolutionNote }) => ({
        url: `/review-items/${id}/resolution`,
        method: 'POST',
        body: { resolutionNote },
      }),
      invalidatesTags: [reviewItemListTag(), reviewQueueSummaryTag()],
    }),

    dismissReviewItem: build.mutation<void, { id: ReviewItemId; resolutionNote: string }>({
      query: ({ id, resolutionNote }) => ({
        url: `/review-items/${id}/dismissal`,
        method: 'POST',
        body: { resolutionNote },
      }),
      invalidatesTags: [reviewItemListTag(), reviewQueueSummaryTag()],
    }),
  }),
});

// ── Part B: disputes ─────────────────────────────────────────────────────────

export type ListDisputesParams = {
  readonly status?: 'open' | 'all' | 'resolved' | 'rejected' | 'escalated' | undefined;
  readonly disputeType?: DisputeType | undefined;
  readonly shopId?: ShopId | undefined;
  readonly raisedBy?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type CreateDisputeBody = {
  readonly disputeType: DisputeType;
  readonly shopId: ShopId;
  readonly saleId?: string | undefined;
  readonly paymentId?: string | undefined;
  readonly inventoryUnitId?: string | undefined;
  readonly shiftId?: string | undefined;
  readonly customerId?: CustomerId | undefined;
  readonly customerName?: string | undefined;
  readonly customerPhone?: string | undefined;
  readonly claim: string;
  readonly observation?: string | undefined;
  readonly requestedOutcome?: string | undefined;
};

export type ResolveDisputeBody = {
  readonly resolution: string;
  readonly resolutionNote: string;
  readonly financialEffectType: string;
  readonly financialEffectKes?: number | undefined;
};

export const disputeApi = api.injectEndpoints({
  endpoints: (build) => ({
    listDisputes: build.query<Paged<Dispute>, ListDisputesParams>({
      query: (params) => ({ url: '/disputes', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [disputeListTag()],
    }),

    getDispute: build.query<Dispute, DisputeId>({
      query: (id) => `/disputes/${id}`,
      providesTags: (_result, _error, id) => [disputeTag(id)],
    }),

    listDisputeNotes: build.query<DisputeNote[], DisputeId>({
      query: (id) => `/disputes/${id}/notes`,
      providesTags: (_result, _error, id) => [disputeNoteListTag(id)],
    }),

    createDispute: build.mutation<DisputeId, CreateDisputeBody>({
      query: (body) => ({ url: '/disputes', method: 'POST', body }),
      invalidatesTags: [disputeListTag()],
    }),

    addDisputeNote: build.mutation<void, { id: DisputeId; body: string }>({
      query: ({ id, body }) => ({ url: `/disputes/${id}/notes`, method: 'POST', body: { body } }),
      invalidatesTags: (_result, _error, { id }) => [disputeNoteListTag(id)],
    }),

    moveDisputeToInvestigating: build.mutation<void, DisputeId>({
      query: (id) => ({ url: `/disputes/${id}/investigation`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [disputeTag(id), disputeListTag()],
    }),

    resolveDispute: build.mutation<void, { id: DisputeId; body: ResolveDisputeBody }>({
      query: ({ id, body }) => ({ url: `/disputes/${id}/resolution`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [disputeTag(id), disputeListTag()],
    }),

    escalateDispute: build.mutation<void, DisputeId>({
      query: (id) => ({ url: `/disputes/${id}/escalation`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [disputeTag(id), disputeListTag()],
    }),

    /** The customer_says_paid auto-search — the highest-value part of the module. */
    searchPaymentDisputeCandidates: build.query<
      PaymentDisputeCandidate[],
      { claimedAmountKes: number; phoneLast4?: string | undefined; claimedAt?: string | undefined }
    >({
      query: (params) => ({ url: '/disputes/candidates/payments', params }),
      keepUnusedDataFor: 0,
    }),

    /** The serial_mismatch auto-search — the full IMEI trail. */
    getUnitHistoryForDispute: build.query<UnitHistory, string>({
      query: (serialNo) => ({ url: '/disputes/candidates/unit-history', params: { serialNo } }),
      keepUnusedDataFor: 0,
    }),
  }),
});

// ── Part C: remote approvals ─────────────────────────────────────────────────

export const approvalApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPendingApprovals: build.query<ApprovalRequest[], { shopId?: ShopId | undefined }>({
      query: (params) => ({ url: '/approval-requests', params }),
      providesTags: [approvalRequestListTag()],
    }),

    /**
     * The requests *this caller* raised that are still pending — the other side of the list
     * above. Needs only `approval.request`, which every role holds, and the server filters on the
     * caller's own id so it can never return anybody else's wait.
     */
    listMyPendingApprovals: build.query<ApprovalRequest[], void>({
      query: () => '/approval-requests/mine',
      providesTags: [approvalRequestListTag()],
    }),

    /** The requester's own device polls this while waiting. */
    getApprovalRequest: build.query<ApprovalRequest, ApprovalRequestId>({
      query: (id) => `/approval-requests/${id}`,
      providesTags: (_result, _error, id) => [approvalRequestTag(id)],
    }),

    approveRequest: build.mutation<void, { id: ApprovalRequestId; note?: string | undefined }>({
      query: ({ id, note }) => ({
        url: `/approval-requests/${id}/approval`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        approvalRequestTag(id),
        approvalRequestListTag(),
      ],
    }),

    rejectRequest: build.mutation<void, { id: ApprovalRequestId; note?: string | undefined }>({
      query: ({ id, note }) => ({
        url: `/approval-requests/${id}/rejection`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        approvalRequestTag(id),
        approvalRequestListTag(),
      ],
    }),

    /** The requester changing their mind — never a trap. */
    cancelApprovalRequest: build.mutation<void, ApprovalRequestId>({
      query: (id) => ({ url: `/approval-requests/${id}/cancellation`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [approvalRequestTag(id), approvalRequestListTag()],
    }),
  }),
});

// ── Part D: brokers and commission ───────────────────────────────────────────

export const commissionApi = api.injectEndpoints({
  endpoints: (build) => ({
    listBrokers: build.query<Broker[], { onlyActive?: boolean | undefined }>({
      query: (params) => ({ url: '/brokers', params }),
      providesTags: [brokerListTag()],
    }),

    createBroker: build.mutation<
      BrokerId,
      {
        name: string;
        phone?: string | undefined;
        idNumber?: string | undefined;
        note?: string | undefined;
      }
    >({
      query: (body) => ({ url: '/brokers', method: 'POST', body }),
      invalidatesTags: [brokerListTag()],
    }),

    getCommissionBalance: build.query<
      CommissionBalance,
      { scope: CommissionScope; subjectId: string }
    >({
      query: (params) => ({ url: '/commission/balance', params }),
      providesTags: (_result, _error, { subjectId }) => [commissionBalanceTag(subjectId)],
    }),

    getCommissionStatement: build.query<
      CommissionStatementLine[],
      {
        scope: CommissionScope;
        subjectId: string;
        from?: string | undefined;
        to?: string | undefined;
      }
    >({
      query: (params) => ({ url: '/commission/statement', params }),
      providesTags: (_result, _error, { subjectId }) => [commissionStatementTag(subjectId)],
    }),

    setCommissionRule: build.mutation<
      string,
      {
        scope: CommissionScope;
        brokerId?: string | undefined;
        userId?: string | undefined;
        variantId?: string | undefined;
        categoryId?: string | undefined;
        shopId?: ShopId | undefined;
        basis: CommissionBasis;
        rateBps?: number | undefined;
        fixedAmountKes?: number | undefined;
        minQty: number;
      }
    >({
      query: (body) => ({ url: '/commission-rules', method: 'POST', body }),
      invalidatesTags: (_result, _error, body) =>
        body.brokerId === undefined ? [] : [brokerCommissionTag(body.brokerId)],
    }),

    /** Ends a rule; with no rule left the broker earns nothing on new sales. */
    closeCommissionRule: build.mutation<void, { id: string; brokerId: string }>({
      query: ({ id }) => ({ url: `/commission-rules/${id}/closure`, method: 'POST' }),
      invalidatesTags: (_result, _error, { brokerId }) => [brokerCommissionTag(brokerId)],
    }),

    listBrokerCommissionRules: build.query<BrokerCommissionRule[], string>({
      query: (brokerId) => `/brokers/${brokerId}/commission-rules`,
      providesTags: (_result, _error, brokerId) => [brokerCommissionTag(brokerId)],
    }),

    getBrokerCommissionBalance: build.query<CommissionBalance, string>({
      query: (brokerId) => `/brokers/${brokerId}/commission-balance`,
      providesTags: (_result, _error, brokerId) => [brokerCommissionTag(brokerId)],
    }),

    listBrokerPayouts: build.query<BrokerPayout[], string>({
      query: (brokerId) => `/brokers/${brokerId}/payouts`,
      providesTags: (_result, _error, brokerId) => [brokerCommissionTag(brokerId)],
    }),

    /** Pays everything ready for the broker and records how, in one step. Owner only. */
    payBrokerNow: build.mutation<
      BrokerPayout,
      {
        brokerId: string;
        method: CommissionPayoutMethod;
        reference?: string | undefined;
        note?: string | undefined;
      }
    >({
      query: ({ brokerId, ...body }) => ({
        url: `/brokers/${brokerId}/payouts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { brokerId }) => [
        brokerCommissionTag(brokerId),
        commissionBalanceTag(brokerId),
      ],
    }),

    createCommissionPayout: build.mutation<
      string,
      {
        scope: CommissionScope;
        brokerId?: string | undefined;
        userId?: string | undefined;
        periodFrom: string;
        periodTo: string;
        method: CommissionPayoutMethod;
        reference?: string | undefined;
        note?: string | undefined;
      }
    >({
      query: (body) => ({ url: '/commission-payouts', method: 'POST', body }),
      invalidatesTags: (_result, _error, body) => [
        commissionBalanceTag(body.brokerId ?? body.userId ?? ''),
      ],
    }),
  }),
});

// ── Part E: customers ────────────────────────────────────────────────────────

export const customerApi = api.injectEndpoints({
  endpoints: (build) => ({
    searchCustomers: build.query<Customer[], { term: string }>({
      query: (params) => ({ url: '/customers', params }),
      providesTags: [customerListTag()],
    }),

    createCustomer: build.mutation<
      CustomerId,
      { name: string; phone?: string | undefined; note?: string | undefined }
    >({
      query: (body) => ({ url: '/customers', method: 'POST', body }),
      invalidatesTags: [customerListTag()],
    }),
  }),
});

export const {
  useListReviewItemsQuery,
  useGetReviewQueueSummaryQuery,
  useResolveReviewItemMutation,
  useDismissReviewItemMutation,
} = reviewApi;

export const {
  useListDisputesQuery,
  useGetDisputeQuery,
  useListDisputeNotesQuery,
  useCreateDisputeMutation,
  useAddDisputeNoteMutation,
  useMoveDisputeToInvestigatingMutation,
  useResolveDisputeMutation,
  useEscalateDisputeMutation,
  useSearchPaymentDisputeCandidatesQuery,
  useGetUnitHistoryForDisputeQuery,
} = disputeApi;

export const {
  useListPendingApprovalsQuery,
  useListMyPendingApprovalsQuery,
  useGetApprovalRequestQuery,
  useApproveRequestMutation,
  useRejectRequestMutation,
  useCancelApprovalRequestMutation,
} = approvalApi;

export const {
  useListBrokersQuery,
  useCreateBrokerMutation,
  useGetCommissionBalanceQuery,
  useGetCommissionStatementQuery,
  useSetCommissionRuleMutation,
  useCloseCommissionRuleMutation,
  useListBrokerCommissionRulesQuery,
  useListBrokerPayoutsQuery,
  useGetBrokerCommissionBalanceQuery,
  usePayBrokerNowMutation,
  useCreateCommissionPayoutMutation,
} = commissionApi;

export const { useSearchCustomersQuery, useCreateCustomerMutation } = customerApi;
